import { makeAutoObservable } from "mobx";
import { V2TranscriptWord } from "../../pages/V2Editor/V2TextEditor";
import { ScriptLayerCut, ScriptLayerSegment, ScriptLayerGap, ScriptLayerSplit, SpeakerOverride, SpeakerMuteRange, TranscriptHiddenRange, TranscriptWordCorrection, TranscriptParagraphBreak, TranscriptMarker } from "../../engine/clips/script/script.interfaces";
import { CompositionService } from "../services/CompositionService";
import { CompositionEventsEnum } from "../CompositionEvents";

function generateId(): string {
	return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

// Default colors for speaker overrides - these should match waveform colors
export const DEFAULT_SPEAKER_COLORS = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

export type ScriptClipItem = {
	id: string;
	clipId: string;
	words: V2TranscriptWord[];
};

export interface SceneInfo {
	id: string;
	index: number;
	startSec: number;
	endSec: number;
	thumbnailUrl: string | null;  // null until generated
}

export type SpeakerWaveformColorSegment = {
	startSec: number;
	endSec: number;
	color: string; // CSS color string
};

export type SpeakerMuteTarget = {
	sourceIndex?: number;
	diarizationSpeakerId?: string;
	speakerId?: string;
	speakerName?: string;
};

class ScriptLayerStoreImpl {
	items: ScriptClipItem[] = [];

	// Visual layer ordering for layout slot assignment (matches V2 "Layers" panel ordering).
	// Stored as LayerRow.id values (e.g. `script:<scriptClipId>:<sourceIndex>` and non-script `clipId`).
	layoutVisualOrderScriptClipId: string | null = null;
	layoutVisualOrderRowIds: string[] = [];
	layoutVisualOrderVersion: number = 0;
	
	// Cuts, gaps, and segments for the script layer clip (observable for UI updates)
	cuts: ScriptLayerCut[] = [];
	gaps: ScriptLayerGap[] = [];
	segments: ScriptLayerSegment[] = [];
	cutsVersion: number = 0; // Increment to force re-renders (covers cuts, gaps, and segments)
	
	// Splits and scenes for scene bar
	splits: ScriptLayerSplit[] = [];
	scenes: SceneInfo[] = [];
	clipDuration: number = 0;
	hasVideo: boolean = false;
	scenesVersion: number = 0; // Increment to force re-renders of scene bar
	
	// Selected cut for restore functionality (when user clicks a strikethrough word)
	selectedCutId: string | null = null;
	
	// Speaker overrides: manual speaker assignments for time ranges
	speakerOverrides: SpeakerOverride[] = [];
	speakerOverridesVersion: number = 0;
	// Speaker-targeted audio mute ranges (separate provenance from gaps).
	speakerMuteRanges: SpeakerMuteRange[] = [];
	speakerMuteRangesVersion: number = 0;

	// Transcript-only edits (do not affect playback)
	transcriptHiddenRanges: TranscriptHiddenRange[] = [];
	transcriptHiddenRangesVersion: number = 0;
	wordCorrections: TranscriptWordCorrection[] = [];
	wordCorrectionsVersion: number = 0;
	transcriptParagraphBreaks: TranscriptParagraphBreak[] = [];
	transcriptParagraphBreaksVersion: number = 0;
	transcriptMarkers: TranscriptMarker[] = [];
	transcriptMarkersVersion: number = 0;

	// Observable gain values per clip (clipId -> gain)
	// This mirrors clip.gain but is observable for MobX reactivity
	clipGains: Map<string, number> = new Map();

	// Lightweight version counter for visual property updates during drag
	// This triggers only sidebar updates, not full waveform re-renders like cutsVersion
	visualPropsVersion: number = 0;

	// Pending speaker override (when user clicks "Change Speaker" and we need to show the edit UI)
	pendingSpeakerOverride: { startSec: number; endSec: number } | null = null;

	// Current playback rate for the script clip (for time translation between transcript and timeline)
	playbackRate: number = 1;

	// Speaker-colored waveform segments for the script clip timeline (derived from transcript words).
	// These are timeline-relative seconds (0..timelineDuration), NOT clip-local source seconds.
	speakerWaveformSegments: SpeakerWaveformColorSegment[] = [];
	speakerWaveformSegmentsVersion: number = 0;

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true });
	}

	ensureLayoutVisualOrder(scriptClipId: string, currentVisualRowIds: string[]): string[] {
		const clipId = String(scriptClipId ?? "");
		const ids = Array.isArray(currentVisualRowIds) ? currentVisualRowIds.map(String) : [];

		if (!clipId) {
			if (this.layoutVisualOrderRowIds.length > 0 || this.layoutVisualOrderScriptClipId != null) {
				this.layoutVisualOrderScriptClipId = null;
				this.layoutVisualOrderRowIds = [];
				this.layoutVisualOrderVersion++;
			}
			return [];
		}

		if (this.layoutVisualOrderScriptClipId !== clipId) {
			this.layoutVisualOrderScriptClipId = clipId;
			this.layoutVisualOrderRowIds = [...ids];
			this.layoutVisualOrderVersion++;
			return this.layoutVisualOrderRowIds;
		}

		const existing = this.layoutVisualOrderRowIds;
		const filtered = existing.filter((id) => ids.includes(id));
		for (const id of ids) {
			if (!filtered.includes(id)) filtered.push(id);
		}

		if (filtered.length !== existing.length || filtered.some((id, i) => id !== existing[i])) {
			this.layoutVisualOrderRowIds = filtered;
			this.layoutVisualOrderVersion++;
		}

		return this.layoutVisualOrderRowIds;
	}

	setLayoutVisualOrder(scriptClipId: string, order: string[]) {
		const clipId = String(scriptClipId ?? "");
		if (!clipId) return;

		if (this.layoutVisualOrderScriptClipId !== clipId) {
			this.layoutVisualOrderScriptClipId = clipId;
		}

		const next = Array.isArray(order) ? order.map(String) : [];
		const prev = this.layoutVisualOrderRowIds;
		if (next.length === prev.length && next.every((id, i) => id === prev[i])) return;

		this.layoutVisualOrderRowIds = next;
		this.layoutVisualOrderVersion++;
	}

	getLayoutVisualOrder(scriptClipId: string): string[] {
		const clipId = String(scriptClipId ?? "");
		if (!clipId) return [];
		if (this.layoutVisualOrderScriptClipId !== clipId) return [];
		return Array.isArray(this.layoutVisualOrderRowIds) ? this.layoutVisualOrderRowIds : [];
	}

	setSpeakerWaveformSegments(segments: SpeakerWaveformColorSegment[]) {
		const next = Array.isArray(segments) ? segments : [];

		const prev = this.speakerWaveformSegments;
		if (prev.length === next.length) {
			let same = true;
			for (let i = 0; i < prev.length; i++) {
				const a = prev[i];
				const b = next[i];
				if (
					a?.startSec !== b?.startSec ||
					a?.endSec !== b?.endSec ||
					a?.color !== b?.color
				) {
					same = false;
					break;
				}
			}
			if (same) return;
		}

		this.speakerWaveformSegments = next;
		this.speakerWaveformSegmentsVersion++;
	}

	clearSpeakerWaveformSegments() {
		if (this.speakerWaveformSegments.length === 0) return;
		this.speakerWaveformSegments = [];
		this.speakerWaveformSegmentsVersion++;
	}

	private syncSpeakerOverridesToEngine(): void {
		if (!CompositionService.isInitialized()) return;
		const clip: any = CompositionService.getScriptLayerClip?.();
		if (!clip || String(clip.type) !== 'script') return;

		try {
			clip.speakerOverrides = Array.isArray(this.speakerOverrides) ? [...this.speakerOverrides] : [];
		} catch {}

		// Notify persistence + undo subscribers.
		try {
			(CompositionService.composition as any)?.trigger?.(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
				script: {
					layerId: (CompositionService.composition as any)?.scriptLayer?.layerId ?? null,
					clipId: clip.id,
					speakerOverridesChanged: true,
				},
			});
		} catch {}
	}

	private syncSpeakerMuteRangesToEngine(): void {
		if (!CompositionService.isInitialized()) return;
		const clip: any = CompositionService.getScriptLayerClip?.();
		if (!clip || String(clip.type) !== 'script') return;

		try {
			if (typeof clip.applyPersistedEdits === "function") {
				clip.applyPersistedEdits({ speakerMuteRanges: Array.isArray(this.speakerMuteRanges) ? [...this.speakerMuteRanges] : [] });
			} else {
				clip.speakerMuteRanges = Array.isArray(this.speakerMuteRanges) ? [...this.speakerMuteRanges] : [];
			}
		} catch {}

		// Notify persistence + undo subscribers.
		try {
			(CompositionService.composition as any)?.trigger?.(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, {
				script: {
					layerId: (CompositionService.composition as any)?.scriptLayer?.layerId ?? null,
					clipId: clip.id,
					speakerMutesChanged: true,
				},
			});
		} catch {}
	}
	
	/**
	 * Update scenes derived from splits.
	 * Called when SCRIPT_LAYER_UPDATED fires with splits info.
	 */
	updateScenes(scenes: SceneInfo[], duration: number, hasVideo: boolean) {
		// Preserve thumbnails when the scene boundaries are unchanged so the UI does not flash
		// on transcript-only edits (cuts/gaps) that don't affect scene timing.
		// Only carry thumbnails forward when the id + [start,end] match (so we never show a
		// thumbnail from an older scene after a split insertion/removal shifts indices).
		const EPS = 1e-3;
		const prevById = new Map<string, SceneInfo>();
		for (const prev of this.scenes) {
			if (!prev?.id) continue;
			prevById.set(prev.id, prev);
		}

		const merged = (scenes ?? []).map((next) => {
			const id = String((next as any)?.id ?? "");
			const prev = prevById.get(id);
			if (!prev) return next;

			const sameTiming =
				Math.abs(Number(prev.startSec ?? 0) - Number(next.startSec ?? 0)) <= EPS &&
				Math.abs(Number(prev.endSec ?? 0) - Number(next.endSec ?? 0)) <= EPS;

			if (!sameTiming) return next;

			const prevThumb = prev.thumbnailUrl;
			const nextThumb = (next as any)?.thumbnailUrl;
			if ((nextThumb == null || nextThumb === "") && prevThumb) {
				return { ...next, thumbnailUrl: prevThumb };
			}

			return next;
		});

		this.scenes = merged;
		this.clipDuration = duration;
		this.hasVideo = hasVideo;
		this.scenesVersion++;
		console.log('[ScriptLayerStore] Updated scenes:', { 
			scenesCount: scenes.length, 
			duration, 
			hasVideo,
			version: this.scenesVersion 
		});
	}
	
	/**
	 * Set thumbnail URL for a specific scene.
	 * Called after screenshots are generated.
	 */
	setSceneThumbnail(sceneId: string, thumbnailUrl: string) {
		const scene = this.scenes.find(s => s.id === sceneId);
		if (scene) {
			if (scene.thumbnailUrl === thumbnailUrl) return;
			scene.thumbnailUrl = thumbnailUrl;
			this.scenesVersion++; // Force re-render
		}
	}
	
	/**
	 * Set the currently selected cut (for restore mode).
	 */
	setSelectedCut(cutId: string | null) {
		this.selectedCutId = cutId;
	}
	
	/**
	 * Get the currently selected cut object.
	 */
	get selectedCut(): ScriptLayerCut | null {
		if (!this.selectedCutId) return null;
		return this.cuts.find(c => c.id === this.selectedCutId) ?? null;
	}
	
	/**
	 * Update cuts, gaps, and segments from the script layer clip.
	 * Called when SCRIPT_LAYER_UPDATED fires.
	 */
	updateCutsAndSegments(cuts: ScriptLayerCut[], segments: ScriptLayerSegment[], gaps?: ScriptLayerGap[]) {
		this.cuts = cuts ?? [];
		this.gaps = gaps ?? [];
		this.segments = segments ?? [];
		this.cutsVersion++;
		console.log('[ScriptLayerStore] Updated cuts, gaps, and segments:', {
			cutsCount: this.cuts.length,
			gapsCount: this.gaps.length,
			segmentsCount: this.segments.length,
			version: this.cutsVersion
		});
	}

	/**
	 * Bump the cutsVersion to force re-renders of dependent components.
	 * Used when scene properties change (gain, etc.) to update waveform display.
	 */
	bumpCutsVersion() {
		this.cutsVersion++;
	}

	/**
	 * Bump the visualPropsVersion to force sidebar re-renders during drag.
	 * This is lighter than bumpCutsVersion - only updates sidebar, not waveforms.
	 */
	bumpVisualPropsVersion() {
		this.visualPropsVersion++;
	}

	/**
	 * Set the playback rate for time translation between transcript and timeline.
	 */
	setPlaybackRate(rate: number) {
		this.playbackRate = rate || 1;
	}

	/**
	 * Check if a given time (clip-local) is within a cut region.
	 * Returns the cut if found, null otherwise.
	 */
	getCutAtTime(time: number): ScriptLayerCut | null {
		for (const cut of this.cuts) {
			if (time >= cut.startSec && time < cut.endSec) {
				return cut;
			}
		}
		return null;
	}
	
	/**
	 * Check if a given time is in a "delete" cut (should be removed from transcript).
	 */
	isTimeInDeleteCut(time: number): boolean {
		const cut = this.getCutAtTime(time);
		return cut !== null && (cut.transcriptMode ?? 'delete') === 'delete';
	}
	
	/**
	 * Check if a given time is in a "strikethrough" cut (should show as strikethrough).
	 */
	isTimeInStrikethroughCut(time: number): boolean {
		const cut = this.getCutAtTime(time);
		return cut !== null && (cut.transcriptMode === 'strikethrough' || cut.transcriptMode === 'retake-strikethrough');
	}

	/**
	 * Check if a given time is in a "retake-strikethrough" cut.
	 */
	isTimeInRetakeCut(time: number): boolean {
		const cut = this.getCutAtTime(time);
		return cut !== null && cut.transcriptMode === 'retake-strikethrough';
	}

	/**
	 * Returns the transcript display mode at this time.
	 */
	getCutTranscriptModeAtTime(time: number): 'delete' | 'strikethrough' | 'retake-strikethrough' | null {
		const cut = this.getCutAtTime(time);
		if (!cut) return null;
		if (cut.transcriptMode === 'retake-strikethrough') return 'retake-strikethrough';
		if (cut.transcriptMode === 'strikethrough') return 'strikethrough';
		return 'delete';
	}
	
	/**
	 * Get cuts that use strikethrough mode (for styling words).
	 */
	get strikethroughCuts(): ScriptLayerCut[] {
		return this.cuts.filter(c => c.transcriptMode === 'strikethrough' || c.transcriptMode === 'retake-strikethrough');
	}

	/**
	 * Get cuts that specifically represent retakes.
	 */
	get retakeCuts(): ScriptLayerCut[] {
		return this.cuts.filter(c => c.transcriptMode === 'retake-strikethrough');
	}
	
	/**
	 * Get cuts that use delete mode.
	 */
	get deleteCuts(): ScriptLayerCut[] {
		return this.cuts.filter(c => (c.transcriptMode ?? 'delete') === 'delete');
	}
	
	/**
	 * Check if a given time is within a gap region.
	 */
	getGapAtTime(time: number): ScriptLayerGap | null {
		for (const gap of this.gaps) {
			if (time >= gap.startSec && time < gap.endSec) {
				return gap;
			}
		}
		return null;
	}
	
	/**
	 * Check if a given time is in a gap.
	 */
	isTimeInGap(time: number): boolean {
		return this.getGapAtTime(time) !== null;
	}
	
	/**
	 * Check if there's a split (scene boundary) between two times.
	 * Used to render "/" in transcript between words.
	 */
	hasSplitBetween(startTime: number, endTime: number): boolean {
		// Scene boundaries are at scene.startSec (except first scene which starts at 0)
		for (const scene of this.scenes) {
			// Check if scene boundary falls between the two times
			if (scene.startSec > startTime && scene.startSec < endTime) {
				console.log('[ScriptLayerStore] Found split between', startTime, 'and', endTime, 'at', scene.startSec);
				return true;
			}
		}
		return false;
	}
	
	/**
	 * Get the split time between two word times, if any.
	 */
	getSplitBetween(startTime: number, endTime: number): number | null {
		for (const scene of this.scenes) {
			if (scene.startSec > startTime && scene.startSec < endTime) {
				return scene.startSec;
			}
		}
		return null;
	}

	/**
	 * Map a source/clip-local time to timeline time.
	 * This accounts for:
	 * 1. Cuts that have removed time (via segments)
	 * 2. PlaybackRate that compresses/expands the timeline
	 *
	 * @param srcTime - Source/clip-local time (transcript word times)
	 * @returns Timeline time (for seeking), or null if time is in a cut region
	 */
	mapSourceTimeToCompositionTime(srcTime: number): number | null {
		const rate = Number(this.playbackRate || 1) || 1;
		const clip: any = CompositionService.isInitialized?.() ? CompositionService.getScriptLayerClip?.() : null;
		const clipStartSec = Number(clip?.start?.seconds ?? 0);
		const clipInSec = Number(clip?.range?.[0]?.seconds ?? 0);
		const clipOutSec = Number(clip?.range?.[1]?.seconds ?? clip?.duration?.seconds ?? clipInSec);
		const EPS = 1e-3;

		// Use store segments when available; otherwise fall back to engine-derived segments.
		const segs: ScriptLayerSegment[] =
			Array.isArray(this.segments) && this.segments.length > 0
				? this.segments
				: (Array.isArray(clip?.scriptSegments) ? (clip.scriptSegments as ScriptLayerSegment[]) : []);

		// Defensive fallback when segments haven't been pushed to the store yet.
		// This avoids "first click" mapping glitches right after restore/init.
		if (!segs.length) {
			if (!Number.isFinite(srcTime)) return null;
			if (clip) {
				// Outside clip range → not mappable
				if (srcTime < clipInSec - EPS) return null;
				// Allow mapping exactly at the end to the current timeline duration.
				if (Math.abs(srcTime - clipOutSec) <= EPS) {
					const dur = Number(clip?.timelineDuration?.seconds ?? 0);
					return Number.isFinite(dur) ? dur : null;
				}
				if (srcTime > clipOutSec + EPS) return null;
				return Math.max(0, (srcTime - clipInSec) / rate);
			}
			// Legacy behavior (best-effort).
			return srcTime / rate;
		}

		// Find which segment contains this source time.
		for (const seg of segs) {
			if (srcTime >= seg.srcStartSec && srcTime < seg.srcEndSec) {
				const offsetIntoSeg = srcTime - seg.srcStartSec;
				const absCompTime = Number(seg.compStartSec) + offsetIntoSeg;
				if (!Number.isFinite(absCompTime)) return null;
				return (absCompTime - clipStartSec) / rate;
			}
		}

		// Handle exact end-of-clip mapping (common during selection).
		if (clip && Number.isFinite(clipOutSec) && Math.abs(srcTime - clipOutSec) <= EPS) {
			const dur = Number(clip?.timelineDuration?.seconds ?? 0);
			return Number.isFinite(dur) ? dur : null;
		}

		// Time is outside all segments (in a cut region or past the end)
		return null;
	}

	/**
	 * Map a source/clip-local time to timeline time, but if the time falls inside a cut
	 * region (i.e. it is not mappable), "snap" it to the next playable segment.
	 *
	 * This is primarily intended for scene/split boundary visualization: a boundary that
	 * lands inside removed content should still be shown at the join point on the timeline.
	 */
	mapSourceTimeToCompositionTimeForBoundary(srcTime: number): number | null {
		const mapped = this.mapSourceTimeToCompositionTime(srcTime);
		if (mapped != null && Number.isFinite(mapped)) return mapped;

		const rate = Number(this.playbackRate || 1) || 1;
		const clip: any = CompositionService.isInitialized?.() ? CompositionService.getScriptLayerClip?.() : null;
		if (!clip || !Number.isFinite(srcTime)) return null;

		const clipStartSec = Number(clip?.start?.seconds ?? 0);
		const clipInSec = Number(clip?.range?.[0]?.seconds ?? 0);
		const clipOutSec = Number(clip?.range?.[1]?.seconds ?? clip?.duration?.seconds ?? clipInSec);
		const EPS = 1e-3;

		// Outside the clip range: keep prior semantics.
		if (srcTime < clipInSec - EPS) return null;
		if (srcTime > clipOutSec + EPS) return null;

		// Use store segments when available; otherwise fall back to engine-derived segments.
		const segs: ScriptLayerSegment[] =
			Array.isArray(this.segments) && this.segments.length > 0
				? this.segments
				: (Array.isArray(clip?.scriptSegments) ? (clip.scriptSegments as ScriptLayerSegment[]) : []);

		// Snap to the first segment that starts at/after this source time.
		let bestSeg: ScriptLayerSegment | null = null;
		let bestSrcStart = Number.POSITIVE_INFINITY;
		for (const seg of segs) {
			const s = Number((seg as any)?.srcStartSec ?? NaN);
			if (!Number.isFinite(s)) continue;
			if (s + EPS < srcTime) continue;
			if (s < bestSrcStart) {
				bestSrcStart = s;
				bestSeg = seg;
			}
		}

		if (bestSeg && Number.isFinite((bestSeg as any)?.compStartSec)) {
			const absCompTime = Number((bestSeg as any).compStartSec);
			return (absCompTime - clipStartSec) / rate;
		}

		// If this is extremely close to the end, snap to end-of-clip.
		if (Math.abs(srcTime - clipOutSec) <= EPS) {
			const dur = Number(clip?.timelineDuration?.seconds ?? 0);
			return Number.isFinite(dur) ? dur : null;
		}

		return null;
	}

	/**
	 * Map a timeline time back to source/clip-local time.
	 * This is the reverse of mapSourceTimeToCompositionTime.
	 * Useful for finding which transcript word the playhead is at.
	 *
	 * @param timelineTime - Timeline time (playhead position)
	 * @returns Source time (for transcript word lookup), or null if in a gap
	 */
	mapTimelineTimeToSourceTime(timelineTime: number): number | null {
		const rate = Number(this.playbackRate || 1) || 1;
		const clip: any = CompositionService.isInitialized?.() ? CompositionService.getScriptLayerClip?.() : null;
		const clipStartSec = Number(clip?.start?.seconds ?? 0);
		const clipInSec = Number(clip?.range?.[0]?.seconds ?? 0);
		const clipOutSec = Number(clip?.range?.[1]?.seconds ?? clip?.duration?.seconds ?? clipInSec);
		const EPS = 1e-3;

		// Convert timeline-relative time to absolute composition time.
		const absCompTime = clipStartSec + timelineTime * rate;

		// Use store segments when available; otherwise fall back to engine-derived segments.
		const segs: ScriptLayerSegment[] =
			Array.isArray(this.segments) && this.segments.length > 0
				? this.segments
				: (Array.isArray(clip?.scriptSegments) ? (clip.scriptSegments as ScriptLayerSegment[]) : []);

		if (!segs.length) {
			// Best-effort fallback when segments haven't been pushed to the store yet.
			if (!clip) return timelineTime * rate;
			const src = clipInSec + timelineTime * rate;
			// If we exceed the source range, we're in inserted silence/gap (no source time).
			if (src < clipInSec - EPS) return null;
			if (src > clipOutSec + EPS) return null;
			// Clamp to end if we're extremely close.
			if (Math.abs(src - clipOutSec) <= EPS) return clipOutSec;
			return src;
		}

		// Find which segment contains this composition time.
		for (const seg of segs) {
			const segDuration = Number(seg.srcEndSec) - Number(seg.srcStartSec);
			const segEndComp = Number(seg.compStartSec) + segDuration;
			if (absCompTime >= Number(seg.compStartSec) && absCompTime < segEndComp) {
				const offsetIntoSeg = absCompTime - Number(seg.compStartSec);
				return Number(seg.srcStartSec) + offsetIntoSeg;
			}
		}

		// Time is outside all segments (in a gap/cut region)
		return null;
	}
	
	/**
	 * Get filtered words that are not in "delete" cut regions or gap regions.
	 * Words in "strikethrough" regions are included (they'll be styled).
	 */
	get visibleTranscriptWords(): V2TranscriptWord[] {
		const out: V2TranscriptWord[] = [];
		for (const it of this.items) {
			if (!Array.isArray(it.words)) continue;
			for (const word of it.words) {
				const time = word.startSec ?? 0;
				// Filter out words in "delete" cuts or gaps
				if (!this.isTimeInDeleteCut(time) && !this.isTimeInGap(time)) {
					out.push(word);
				}
			}
		}
		return out;
	}

	// ============ Speaker Override Methods ============
	
	/**
	 * Set a pending speaker override (used by Change Speaker feature).
	 * The UI will show an editable speaker label for this range.
	 */
	setPendingSpeakerOverride(range: { startSec: number; endSec: number } | null) {
		this.pendingSpeakerOverride = range;
	}
	
	/**
	 * Add a speaker override for a time range.
	 * @param startSec - Start time of the override range
	 * @param endSec - End time of the override range
	 * @param speakerName - Name to display for this speaker
	 * @param color - Optional color (will auto-assign if not provided)
	 * @param usedSourceColors - Optional array of colors already in use by sources (for smart color assignment)
	 * @param speakerId - Optional reference to speaker entity in Firestore
	 * @param diarizationSpeakerId - Optional diarization speaker ID (e.g., "speaker_0") to match specific speaker
	 * @param sourceIndex - Optional transcript source index (required for diarization IDs across sources)
	 */
	addSpeakerOverride(
		startSec: number,
		endSec: number,
		speakerName: string,
		color?: string,
		usedSourceColors: string[] = [],
		speakerId?: string,
		diarizationSpeakerId?: string,
		sourceIndex?: number
	) {
		// Auto-assign color if not provided
		const assignedColor = color || this.getNextSpeakerColor(usedSourceColors);
		
		// Handle overlapping overrides for the SAME diarization key (sourceIndex + diarizationSpeakerId).
		// Overrides for different diarization speakers or different sources are allowed to overlap in time.
		const overlappingOverrides = this.speakerOverrides.filter(
			o =>
				o.startSec < endSec &&
				o.endSec > startSec &&
				o.sourceIndex === sourceIndex &&
				o.diarizationSpeakerId === diarizationSpeakerId
		);
		
		for (const existing of overlappingOverrides) {
			// Case 1: New override is completely inside existing override - split existing
			if (existing.startSec < startSec && existing.endSec > endSec) {
				console.log('[ScriptLayerStore] Splitting existing override:', existing);
				// Create "after" part first (before removing/modifying)
				const afterPart: SpeakerOverride = {
					id: generateId(),
					startSec: endSec,
					endSec: existing.endSec,
					sourceIndex: existing.sourceIndex,
					speakerName: existing.speakerName,
					color: existing.color,
					speakerId: existing.speakerId,
					diarizationSpeakerId: existing.diarizationSpeakerId,
				};
				this.speakerOverrides.push(afterPart);
				
				// Modify existing to be the "before" part
				existing.endSec = startSec;
				console.log('[ScriptLayerStore] Created split: before until', startSec, 'after from', endSec);
			}
			// Case 2: New override starts before existing ends - trim existing end
			else if (existing.startSec < startSec && existing.endSec > startSec) {
				console.log('[ScriptLayerStore] Trimming end of existing override:', existing.endSec, '->', startSec);
				existing.endSec = startSec;
			}
			// Case 3: New override ends after existing starts - trim existing start
			else if (existing.startSec < endSec && existing.endSec > endSec) {
				console.log('[ScriptLayerStore] Trimming start of existing override:', existing.startSec, '->', endSec);
				existing.startSec = endSec;
			}
			// Case 4: New override completely covers existing - remove existing
			else if (startSec <= existing.startSec && endSec >= existing.endSec) {
				console.log('[ScriptLayerStore] Removing completely covered override:', existing);
				const idx = this.speakerOverrides.indexOf(existing);
				if (idx !== -1) {
					this.speakerOverrides.splice(idx, 1);
				}
			}
		}
		
		// Remove any overrides that have become zero-length or invalid
		this.speakerOverrides = this.speakerOverrides.filter(o => o.endSec > o.startSec);
		
		const override: SpeakerOverride = {
			id: generateId(),
			startSec,
			endSec,
			sourceIndex,
			speakerName,
			color: assignedColor,
			speakerId,
			diarizationSpeakerId,
		};
		
		this.speakerOverrides.push(override);
		this.speakerOverrides.sort((a, b) => a.startSec - b.startSec);
		
		// Merge adjacent overrides with the same speakerId
		this.mergeAdjacentOverrides();
		
		this.speakerOverridesVersion++;
		this.pendingSpeakerOverride = null;
		this.syncSpeakerOverridesToEngine();
		
		console.log('[ScriptLayerStore] Added speaker override:', override);
		console.log('[ScriptLayerStore] All overrides now:', this.speakerOverrides.map(o => ({
			id: o.id,
			name: o.speakerName,
			speakerId: o.speakerId,
			color: o.color,
			start: o.startSec,
			end: o.endSec,
		})));
		return override;
	}
	
	/**
	 * Merge adjacent overrides that have the same speakerId (Firestore entity).
	 * This creates a cleaner representation when consecutive sections have the same speaker.
	 */
	private mergeAdjacentOverrides() {
		if (this.speakerOverrides.length < 2) return;
		
		// Sort by start time first
		this.speakerOverrides.sort((a, b) => a.startSec - b.startSec);
		
		const merged: SpeakerOverride[] = [];
		let current = this.speakerOverrides[0];
		
		for (let i = 1; i < this.speakerOverrides.length; i++) {
			const next = this.speakerOverrides[i];
			
			// Check if they can be merged:
			// 1. Same speakerId (both must have speakerId for merge)
			// 2. Adjacent or overlapping (next starts at or before current ends)
			// 3. Same diarization key (sourceIndex + diarizationSpeakerId) to avoid cross-speaker merges
			const sameSpeaker = current.speakerId && next.speakerId && current.speakerId === next.speakerId;
			const sameDiarizationKey =
				current.sourceIndex === next.sourceIndex &&
				current.diarizationSpeakerId === next.diarizationSpeakerId;
			const adjacent = next.startSec <= current.endSec + 0.1; // Small tolerance for floating point
			
			if (sameSpeaker && sameDiarizationKey && adjacent) {
				// Merge: extend current to include next
				console.log('[ScriptLayerStore] Merging adjacent overrides with same speaker:', current.speakerName, current.startSec, '-', current.endSec, 'and', next.startSec, '-', next.endSec);
				current = {
					...current,
					endSec: Math.max(current.endSec, next.endSec),
				};
			} else {
				// Can't merge: save current and move to next
				merged.push(current);
				current = next;
			}
		}
		
		// Don't forget the last one
		merged.push(current);
		
		if (merged.length < this.speakerOverrides.length) {
			console.log('[ScriptLayerStore] Merged', this.speakerOverrides.length, 'overrides into', merged.length);
		}
		
		this.speakerOverrides = merged;
	}
	
	/**
	 * Update an existing speaker override.
	 */
	updateSpeakerOverride(id: string, updates: Partial<Pick<SpeakerOverride, 'speakerName' | 'color' | 'speakerId'>>) {
		const override = this.speakerOverrides.find(o => o.id === id);
		if (override) {
			if (updates.speakerName !== undefined) override.speakerName = updates.speakerName;
			if (updates.color !== undefined) override.color = updates.color;
			if (updates.speakerId !== undefined) override.speakerId = updates.speakerId;
			
			// After updating, try to merge adjacent overrides
			this.mergeAdjacentOverrides();
			
			this.speakerOverridesVersion++;
			this.syncSpeakerOverridesToEngine();
		}
	}
	
	/**
	 * Remove a speaker override by ID.
	 */
	removeSpeakerOverride(id: string) {
		const idx = this.speakerOverrides.findIndex(o => o.id === id);
		if (idx >= 0) {
			this.speakerOverrides.splice(idx, 1);
			this.speakerOverridesVersion++;
			this.syncSpeakerOverridesToEngine();
			console.log('[ScriptLayerStore] Removed speaker override:', id);
		}
	}
	
	/**
	 * Get speaker override for a given time, if any.
	 */
	getSpeakerOverrideAtTime(time: number): SpeakerOverride | null {
		for (const override of this.speakerOverrides) {
			if (time >= override.startSec && time < override.endSec) {
				return override;
			}
		}
		return null;
	}

	private normalizeSpeakerMuteRanges(ranges: SpeakerMuteRange[]): SpeakerMuteRange[] {
		const out: SpeakerMuteRange[] = [];
		for (const r of ranges ?? []) {
			const id = String((r as any)?.id ?? generateId());
			const startSec = Number((r as any)?.startSec ?? NaN);
			const endSec = Number((r as any)?.endSec ?? NaN);
			if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec) continue;
			const sourceIndex = Number.isFinite((r as any)?.sourceIndex) ? Number((r as any).sourceIndex) : undefined;
			const diarizationSpeakerId =
				typeof (r as any)?.diarizationSpeakerId === "string" && String((r as any).diarizationSpeakerId).trim()
					? String((r as any).diarizationSpeakerId).trim()
					: undefined;
			const speakerId =
				typeof (r as any)?.speakerId === "string" && String((r as any).speakerId).trim()
					? String((r as any).speakerId).trim()
					: undefined;
			const speakerName =
				typeof (r as any)?.speakerName === "string" && String((r as any).speakerName).trim()
					? String((r as any).speakerName).trim()
					: undefined;
			out.push({ id, startSec, endSec, sourceIndex, diarizationSpeakerId, speakerId, speakerName });
		}
		out.sort((a, b) => a.startSec - b.startSec || a.endSec - b.endSec || String(a.id).localeCompare(String(b.id)));
		return out;
	}

	private speakerMuteTargetMatchesRange(target: SpeakerMuteTarget, range: SpeakerMuteRange): boolean {
		const targetSpeakerId = String(target?.speakerId ?? "").trim();
		if (targetSpeakerId) {
			const rangeSpeakerId = String((range as any)?.speakerId ?? "").trim();
			return !!rangeSpeakerId && rangeSpeakerId === targetSpeakerId;
		}

		const targetSourceIndex = Number.isFinite(target?.sourceIndex) ? Number(target?.sourceIndex) : undefined;
		const targetDiarizationSpeakerId = String(target?.diarizationSpeakerId ?? "").trim();
		if (targetSourceIndex != null && targetDiarizationSpeakerId) {
			const rangeSourceIndex = Number.isFinite((range as any)?.sourceIndex) ? Number((range as any).sourceIndex) : undefined;
			const rangeDiarizationSpeakerId = String((range as any)?.diarizationSpeakerId ?? "").trim();
			return rangeSourceIndex === targetSourceIndex && !!rangeDiarizationSpeakerId && rangeDiarizationSpeakerId === targetDiarizationSpeakerId;
		}

		const targetName = String(target?.speakerName ?? "").trim().toLowerCase();
		if (targetSourceIndex != null && targetName) {
			const rangeSourceIndex = Number.isFinite((range as any)?.sourceIndex) ? Number((range as any).sourceIndex) : undefined;
			const rangeName = String((range as any)?.speakerName ?? "").trim().toLowerCase();
			return rangeSourceIndex === targetSourceIndex && !!rangeName && rangeName === targetName;
		}

		return false;
	}

	getSpeakerMuteRangesForTarget(target: SpeakerMuteTarget): SpeakerMuteRange[] {
		return (this.speakerMuteRanges ?? []).filter((r) => this.speakerMuteTargetMatchesRange(target, r));
	}

	isSpeakerMuted(target: SpeakerMuteTarget): boolean {
		return this.getSpeakerMuteRangesForTarget(target).length > 0;
	}

	isSpeakerMutedAtSegment(target: SpeakerMuteTarget, segmentStartSec: number, segmentEndSec: number): boolean {
		const start = Number(segmentStartSec ?? NaN);
		const end = Number(segmentEndSec ?? NaN);
		if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return false;
		for (const r of this.getSpeakerMuteRangesForTarget(target)) {
			const rs = Number((r as any)?.startSec ?? NaN);
			const re = Number((r as any)?.endSec ?? NaN);
			if (!Number.isFinite(rs) || !Number.isFinite(re) || re <= rs) continue;
			if (re > start && rs < end) return true;
		}
		return false;
	}

	setSpeakerMuted(target: SpeakerMuteTarget, ranges: Array<{ startSec: number; endSec: number }>): SpeakerMuteRange[] {
		const targetSpeakerId = String(target?.speakerId ?? "").trim() || undefined;
		const targetDiarizationSpeakerId = String(target?.diarizationSpeakerId ?? "").trim() || undefined;
		const targetSpeakerName = String(target?.speakerName ?? "").trim() || undefined;
		const targetSourceIndex = Number.isFinite(target?.sourceIndex) ? Number(target?.sourceIndex) : undefined;

		const kept = (this.speakerMuteRanges ?? []).filter((r) => !this.speakerMuteTargetMatchesRange(target, r));
		const nextRaw: SpeakerMuteRange[] = [...kept];
		for (const range of ranges ?? []) {
			const startSec = Number((range as any)?.startSec ?? NaN);
			const endSec = Number((range as any)?.endSec ?? NaN);
			if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec) continue;
			nextRaw.push({
				id: generateId(),
				startSec,
				endSec,
				sourceIndex: targetSourceIndex,
				diarizationSpeakerId: targetDiarizationSpeakerId,
				speakerId: targetSpeakerId,
				speakerName: targetSpeakerName,
			});
		}

		this.speakerMuteRanges = this.normalizeSpeakerMuteRanges(nextRaw);
		this.speakerMuteRangesVersion++;
		this.syncSpeakerMuteRangesToEngine();
		return this.getSpeakerMuteRangesForTarget(target);
	}

	clearSpeakerMuted(target: SpeakerMuteTarget): number {
		const before = this.speakerMuteRanges.length;
		this.speakerMuteRanges = (this.speakerMuteRanges ?? []).filter((r) => !this.speakerMuteTargetMatchesRange(target, r));
		const removed = before - this.speakerMuteRanges.length;
		if (removed > 0) {
			this.speakerMuteRangesVersion++;
			this.syncSpeakerMuteRangesToEngine();
		}
		return removed;
	}
	
	/**
	 * Get the next available speaker color (one not currently in use).
	 * Considers both speaker override colors AND original source colors.
	 */
	getNextSpeakerColor(usedSourceColors: string[] = []): string {
		// Collect all used colors from both overrides and provided source colors
		const usedColors = new Set<string>();
		
		// Add override colors
		for (const o of this.speakerOverrides) {
			if (o.color) usedColors.add(o.color);
		}
		
		// Add source colors passed in
		for (const c of usedSourceColors) {
			if (c) usedColors.add(c);
		}
		
		console.log('[ScriptLayerStore] getNextSpeakerColor - used colors:', Array.from(usedColors));
		
		// Find first unused color
		for (const color of DEFAULT_SPEAKER_COLORS) {
			if (!usedColors.has(color)) {
				console.log('[ScriptLayerStore] getNextSpeakerColor - returning unused:', color);
				return color;
			}
		}
		
		// All colors used, cycle back based on total count
		const totalUsed = usedColors.size;
		const cycledColor = DEFAULT_SPEAKER_COLORS[totalUsed % DEFAULT_SPEAKER_COLORS.length];
		console.log('[ScriptLayerStore] getNextSpeakerColor - all used, cycling to:', cycledColor);
		return cycledColor;
	}
	
	/**
	 * Update speaker overrides from the ScriptLayerClip.
	 * Called when SCRIPT_LAYER_UPDATED fires.
	 */
	updateSpeakerOverrides(overrides: SpeakerOverride[]) {
		this.speakerOverrides = overrides ?? [];
		this.speakerOverridesVersion++;
	}

	updateSpeakerMuteRanges(ranges: SpeakerMuteRange[]) {
		this.speakerMuteRanges = this.normalizeSpeakerMuteRanges(ranges ?? []);
		this.speakerMuteRangesVersion++;
	}

	updateTranscriptHiddenRanges(ranges: TranscriptHiddenRange[]) {
		this.transcriptHiddenRanges = ranges ?? [];
		this.transcriptHiddenRangesVersion++;
	}

	updateWordCorrections(corrections: TranscriptWordCorrection[]) {
		this.wordCorrections = corrections ?? [];
		this.wordCorrectionsVersion++;
	}

	updateTranscriptParagraphBreaks(breaks: TranscriptParagraphBreak[]) {
		this.transcriptParagraphBreaks = breaks ?? [];
		this.transcriptParagraphBreaksVersion++;
	}

	updateTranscriptMarkers(markers: TranscriptMarker[]) {
		this.transcriptMarkers = markers ?? [];
		this.transcriptMarkersVersion++;
	}

	getTranscriptHiddenRangeAtTime(time: number): TranscriptHiddenRange | null {
		for (const r of this.transcriptHiddenRanges) {
			if (time >= r.startSec && time < r.endSec) return r;
		}
		return null;
	}

	isTimeInTranscriptHiddenRange(time: number): boolean {
		return this.getTranscriptHiddenRangeAtTime(time) !== null;
	}

	getWordCorrectionText(args: { sourceIndex?: number; srcStartSec?: number; srcEndSec?: number }): string | null {
		const sourceIndex = Number.isFinite(args.sourceIndex) ? Number(args.sourceIndex) : undefined;
		const srcStartSec = Number.isFinite(args.srcStartSec) ? Number(args.srcStartSec) : undefined;
		const srcEndSec = Number.isFinite(args.srcEndSec) ? Number(args.srcEndSec) : undefined;

		for (const c of this.wordCorrections) {
			if (sourceIndex != null && Number.isFinite(c.sourceIndex) && Number(c.sourceIndex) !== sourceIndex) continue;
			if (srcStartSec != null && Number.isFinite(c.srcStartSec) && Number(c.srcStartSec) !== srcStartSec) continue;
			if (srcEndSec != null && Number.isFinite(c.srcEndSec) && Number(c.srcEndSec) !== srcEndSec) continue;
			if (c.correctedText) return c.correctedText;
		}
		return null;
	}

	/**
	 * Set the gain for a clip (observable for MobX reactivity).
	 * Called from V2PropertiesSidebar when volume slider changes.
	 */
	setClipGain(clipId: string, gain: number) {
		this.clipGains.set(clipId, gain);
	}

	/**
	 * Get the gain for a clip (returns 1.0 if not set).
	 */
	getClipGain(clipId: string): number {
		return this.clipGains.get(clipId) ?? 1.0;
	}

	clear() {
		this.items = [];
		this.cuts = [];
		this.gaps = [];
		this.segments = [];
		this.cutsVersion = 0;
		this.selectedCutId = null;
		this.splits = [];
		this.scenes = [];
		this.clipDuration = 0;
		this.hasVideo = false;
		this.scenesVersion = 0;
		this.speakerOverrides = [];
		this.speakerOverridesVersion = 0;
		this.speakerMuteRanges = [];
		this.speakerMuteRangesVersion = 0;
		this.transcriptHiddenRanges = [];
		this.transcriptHiddenRangesVersion = 0;
		this.wordCorrections = [];
		this.wordCorrectionsVersion = 0;
		this.transcriptParagraphBreaks = [];
		this.transcriptParagraphBreaksVersion = 0;
		this.transcriptMarkers = [];
		this.transcriptMarkersVersion = 0;
		this.pendingSpeakerOverride = null;
		this.clipGains.clear();
		this.visualPropsVersion = 0;
	}

	addClip(input: { clipId: string; words: V2TranscriptWord[] }) {
		const item: ScriptClipItem = {
			id: generateId(),
			clipId: input.clipId,
			words: input.words ?? [],
		};
		this.items.push(item);
		return item.id;
	}

	setClipWords(clipId: string, words: V2TranscriptWord[]) {
		const item = this.items.find(it => it.clipId === clipId);
		if (item) {
			item.words = words ?? [];
			return item.id;
		}
		return this.addClip({ clipId, words });
	}

	hasClip(clipId: string): boolean {
		return this.items.some((it) => it.clipId === clipId);
	}

	removeClip(id: string) {
		this.items = this.items.filter((it) => it.id !== id);
	}

	removeByClipId(clipId: string) {
		this.items = this.items.filter((it) => it.clipId !== clipId);
	}

	reorderByClipIds(order: string[]) {
		const idToItem = new Map(this.items.map((it) => [it.clipId, it]));
		const reordered: ScriptClipItem[] = [];
		for (const clipId of order) {
			const item = idToItem.get(clipId);
			if (item) reordered.push(item);
		}
		// Append any items not present in the order (defensive)
		for (const it of this.items) {
			if (!order.includes(it.clipId)) reordered.push(it);
		}
		this.items = reordered;
	}

	moveClip(id: string, toIndex: number) {
		const fromIndex = this.items.findIndex((it) => it.id === id);
		if (fromIndex < 0) return;
		const [item] = this.items.splice(fromIndex, 1);
		const clamped = Math.max(0, Math.min(toIndex, this.items.length));
		this.items.splice(clamped, 0, item);
	}

	get transcriptWords(): V2TranscriptWord[] {
		const out: V2TranscriptWord[] = [];
		for (const it of this.items) {
			if (Array.isArray(it.words) && it.words.length) {
				out.push(...it.words);
			}
		}
		return out;
	}
}

export const ScriptLayerStore = new ScriptLayerStoreImpl();
